import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronRight, Sparkles, AlertTriangle, Clock, Info, ArrowRight } from "lucide-react";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { SITE, breadcrumb, graph, AUTHOR_REF, PUBLISHER_REF } from "../lib/schema";
import { SYMPTOM_PAGES, SYMPTOM_PAGE_SLUGS } from "../data/symptomPagesContent";
import { SC_CREDIT_LINE, SC_DISCLAIMER_LONG } from "../data/symptomCheckerCredits";
import MedicalDisclaimer from "../components/site/MedicalDisclaimer";

const URGENCY_COLOR = {
  Emergency: "text-red-500 border-red-500/30 bg-red-500/8",
  Urgent:    "text-orange-500 border-orange-500/30 bg-orange-500/8",
  Soon:      "text-amber-500 border-amber-500/30 bg-amber-500/8",
  Routine:   "text-emerald-500 border-emerald-500/30 bg-emerald-500/8",
};

const SymptomPage = () => {
  const { slug } = useParams();
  const data = SYMPTOM_PAGES[slug];

  usePageMeta({
    title: data?.metaTitle || "Symptom Guide | Shantanu Netralaya, Varanasi",
    path: `/symptoms/${slug}`,
    description: data?.metaDescription || "",
  });

  usePageJsonLd(
    data
      ? graph([
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Symptom Guides", path: "/symptom-checker" },
            { name: data.h1, path: `/symptoms/${slug}` },
          ]),
          {
            "@type": "MedicalWebPage",
            "@id": `${SITE}/symptoms/${slug}#page`,
            url: `${SITE}/symptoms/${slug}`,
            name: data.h1,
            description: data.metaDescription,
            inLanguage: "en-IN",
            author: AUTHOR_REF,
            reviewedBy: AUTHOR_REF,
            publisher: PUBLISHER_REF,
            medicalAudience: "https://schema.org/Patient",
          },
          {
            "@type": "FAQPage",
            "@id": `${SITE}/symptoms/${slug}#faq`,
            mainEntity: (data.faqs || []).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ])
      : null
  );

  if (!data) return <Navigate to="/symptom-checker" replace />;

  return (
    <main className="pb-24" data-testid={`symptom-page-${slug}`}>
      <section className="pt-14 sm:pt-20 pb-10 container-page">
        <nav className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50 mb-6 flex items-center gap-2">
          <Link to="/" className="hover:text-[hsl(var(--foreground))]">Home</Link>
          <ChevronRight size={11} />
          <Link to="/symptom-checker" className="hover:text-[hsl(var(--foreground))]">Symptom Guides</Link>
          <ChevronRight size={11} />
          <span className="text-[hsl(var(--accent))]">{data.h1}</span>
        </nav>

        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--accent))]">
            <Sparkles size={12} strokeWidth={1.8} /> Symptom Guide
          </div>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl leading-[1.06] text-balance" data-testid="symptom-h1">
            {data.h1}
          </h1>
          <p className="mt-6 prose-elegant">{data.lead}</p>
        </div>

        <div className="mt-10">
          <Link to="/symptom-checker" className="btn-primary" data-testid="symptom-page-cta">
            Run the free 60-second checker <ArrowRight size={16} strokeWidth={1.7} />
          </Link>
        </div>
      </section>

      {/* Causes */}
      <section className="container-page py-14 border-t border-border">
        <div className="max-w-3xl">
          <div className="overline">Possible causes</div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl leading-tight text-balance">
            What can cause this — and how each feels different.
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.causes.map((c) => (
            <div key={c.title} className="rounded-2xl border border-border p-5">
              <h3 className="font-serif text-lg text-[hsl(var(--foreground))]">{c.title}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[hsl(var(--foreground))]/75">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Urgency guide */}
      <section className="container-page py-14 border-t border-border">
        <div className="max-w-3xl">
          <div className="overline flex items-center gap-2"><Clock size={12} /> Urgency guide</div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl leading-tight text-balance">
            When to seek care — a practical guide.
          </h2>
          <p className="mt-4 prose-elegant">
            Use the guide below only as a starting point. If you are unsure, run the free AI Symptom Checker or
            simply message us on WhatsApp — we would rather see you and reassure you than miss something serious.
          </p>
        </div>
        <div className="mt-8 space-y-3 max-w-3xl">
          {data.urgency.map((u) => (
            <div
              key={u.level}
              className={`rounded-2xl border px-5 py-4 flex items-start gap-3 ${URGENCY_COLOR[u.level] || URGENCY_COLOR.Routine}`}
              data-testid={`urgency-${u.level.toLowerCase()}`}
            >
              <AlertTriangle size={16} className="mt-1 shrink-0" strokeWidth={1.8} />
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-medium">{u.level}</div>
                <div className="mt-1 text-[14px] text-[hsl(var(--foreground))]/85">{u.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="container-page py-14 border-t border-border">
        <div className="max-w-3xl">
          <div className="overline">Frequently asked</div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl leading-tight text-balance">
            Questions patients ask us most.
          </h2>
        </div>
        <div className="mt-8 max-w-3xl divide-y divide-border border-t border-border">
          {data.faqs.map((f, i) => (
            <details key={f.q} className="group py-5" data-testid={`symptom-faq-${i}`} {...(i === 0 ? { open: true } : {})}>
              <summary className="cursor-pointer flex items-start justify-between gap-6 text-[15px] font-medium list-none">
                <span>{f.q}</span>
                <ChevronRight size={16} className="mt-1 shrink-0 text-[hsl(var(--accent))] transition-transform group-open:rotate-90" strokeWidth={2} />
              </summary>
              <p className="mt-4 text-[14.5px] leading-relaxed text-[hsl(var(--foreground))]/80">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Related */}
      <section className="container-page py-14 border-t border-border">
        <div className="overline">Related symptom guides</div>
        <div className="mt-6 flex flex-wrap gap-2">
          {SYMPTOM_PAGE_SLUGS.filter((s) => s !== slug).map((s) => (
            <Link
              key={s}
              to={`/symptoms/${s}`}
              className="text-[13px] px-3.5 py-1.5 rounded-full border border-border hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors"
              data-testid={`related-${s}`}
            >
              {SYMPTOM_PAGES[s].h1.split(" — ")[0]}
            </Link>
          ))}
        </div>
      </section>

      <div className="container-page mt-14">
        <div className="rounded-2xl border border-border bg-[hsl(var(--foreground))]/[0.02] px-5 py-4 flex items-start gap-3 max-w-3xl">
          <Info size={16} className="mt-1 text-[hsl(var(--accent))] shrink-0" strokeWidth={1.8} />
          <p className="text-[12.5px] leading-relaxed text-[hsl(var(--foreground))]/75">
            {SC_DISCLAIMER_LONG}
            <span className="block mt-2 text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50">
              {SC_CREDIT_LINE}
            </span>
          </p>
        </div>
        <div className="mt-6"><MedicalDisclaimer /></div>
      </div>
    </main>
  );
};

export default SymptomPage;
